//Config
//Input data
//

import React, { useRef, useState, useEffect } from 'react';
import GenericCodeLayer, {
  GenericCodeLayerProps
} from './code-layer/code-layer';
import InvoiceMetadata from './invoice-metadata/invoice-metadatav2';
import { Box } from '@chakra-ui/react';
import {
  AmplifyState,
  cmxTypeguards,
  dragManager
} from '@codametrix/ui-common';
import { commonEnums, conditionalUtils, gradeBenchPanelActions } from '@codametrix/ui-common';
import {
  DraggableList,
  HorizontalTabContainer
} from '@codametrix/ui-components';
import { Lookup, shouldShowResults } from '@codametrix/ui-components';
import { workBench, AmplifyCore } from '@codametrix/ui-common';
import InvoiceCard from './invoice-components/invoice-card';
import { sortAndMap } from '../../utils/SortAndMap';
import { Icon, CMXIcon } from '@codametrix/ui-components';
import CaseQuestionsForm from './case-questions-form';
import { ScoreDetailPanel } from './score-detail-panel/ScoreDetailPanel';
import GradeBenchResolverModal from './grade-bench.resolver-modal';
import './styles/index.scss';
const { changeValueAtPath } = workBench.pathUtils;
const { isSelectionCPT, isSelectionICD, isSelectionModifier } =
  AmplifyCore.transactionUpdateDTOFactory.codePredicates;

const draggedCodesManager = dragManager.load('codes');

const vanillaProcedure: Accelerate.CQAProcedure = {
  id: 2, // Unique identifier for the procedure
  code: '99213', // CPT code for an outpatient office visit (Level 3)
  original: true,
  codeAssessment: {
    assessment: 'IDEAL CPT',
    role: 'CODABLE',
    id: 1
  },
  sequenceNumber: 1,
  units: 1,
  codeType: 'procedure',
  description: 'someDescription'
};

const { FieldType } = commonEnums;
const { ChargeSessionCodeType } = commonEnums;


export const isCodeUniqueInCQATransaction = (
  codeInfo: CMxCommonApp.CodeInfo,
  transaction: Accelerate.CQATransaction
): boolean => {
  if (isSelectionCPT(codeInfo)) {
    return !transaction.procedures.some((code) => code.code === codeInfo.code);
  }

  if (isSelectionICD(codeInfo)) {
    return !transaction.diagnoses.some((code) => code.code === codeInfo.code);
  }

  if (isSelectionModifier(codeInfo)) {
    return !transaction.modifiers.some((code) => code.code === codeInfo.code);
  }

  return true;
};

type DictionaryOptions = {
  [key: string]: CMxAPI.Cell[];
};

type WorkflowValidationResult = {
  isValid: boolean;
  errors: string[];
};

export type GradeBenchPanelProps = {
  //Input to gradebench panel is either codes or codableInvoices
  gradeableInvoices: Accelerate.CQAInvoice[];

  //Code Search
  codeSearch?: CQA.CodeSearchProps;

  //Shared Dictionary Results
  //dictionaryResults?: DictionaryOptions;

  //Top Component
  //Bottom Component

  //codes?: CMxCommonApp.ChargeSessionCode[];
  //invoiceDefinition: GroupLevelProps;
  //transactionDefinition: GroupLevelProps;
  //codes: GenericCodeLayerProps;

  //InvoiceMetadataFields
  invoiceMetadataFields: CMxCommonApp.TextField[];
  cptFields: CMxCommonApp.CodeLayerField[];
  icdFields: CMxCommonApp.CodeLayerField[];
  modifierFields: CMxCommonApp.CodeLayerField[];
  //onCodeSearchSelection: (searchSelection: CMxCommonApp.CodeInfo) => {},
  onCodeUpdate: (update: CMxCommonApp.GradeBenchSelectEvent) => void;
  onCodeEvent: (update: CMxCommonApp.GradeBenchCodeEvent) => void;
  onCodeRemove: (changeDetail: Accelerate.DeleteCQACodeParams) => void;
  projectConfiguration: CMxAPI.CQAProjectConfiguration;
  riskCategories: Accelerate.RiskCategoryTree;
  cqaCodeAssessmentRiskDictionaries: Accelerate.CQACodeAssessmentRiskDictionaries;
  onCreateCQATransaction: (
    createCQATransction: Accelerate.CreateCQATransactionParams
  ) => void;
  onDeleteCQATransaction: (
    deleteCQATransction: Accelerate.DeleteCQATransactionParams
  ) => void;
  gradingErrors: {
    addTransaction: boolean;
  };
  resolverValidationErrors?: CQA.ValidationErrorDto[] | null;
  workflowValidationResult?: WorkflowValidationResult | null;
  editable: boolean;
  capabilities?: Accelerate.CapabilitiesState<any>;
};

function attachHandlersToFields(
  fields: CMxCommonApp.CodeLayerField[],
  transaction: Accelerate.CQATransaction,
  invoice: Accelerate.CQAInvoice,
  codeType: CMxCommonApp.ChargeSessionCodeType,
  onChange: (update: CMxCommonApp.GradeBenchSelectEvent) => void,
  onCodeEvent: (update: CMxCommonApp.GradeBenchCodeEvent) => void,
  deleteCQACode: (params: Accelerate.DeleteCQACodeParams) => void
): CMxCommonApp.CodeLayerField[] {
  return fields.map((field) => {
    if (field.fieldType == FieldType.SelectList) {
      const handleUpdate = (key: string[], value: string, values: object) => {
        const update: CMxCommonApp.GradeBenchSelectEvent = {
          selection: value,
          context: {
            value: values,
            field,
            fields,
            transaction
          }
        };
        onChange(update);
      };

      return { ...field, onSelect: handleUpdate };
    }
    if (field.fieldType == FieldType.Code) {
      const handleDelete = (values: Accelerate.CQACode) => {
        const update: Accelerate.DeleteCQACodeParams = {
          code: { ...values },
          codeType,
          context: { transaction, invoice }
        };
        deleteCQACode(update);
      };
      return { ...field, onCodeDelete: handleDelete };
    }

    if (field.fieldType == FieldType.TextInput) {
      const handleTextChange = (
        units: number,
        value: object,
        field: CMxCommonApp.TextInputField
      ) => {
        const update: CMxCommonApp.GradeBenchCodeEvent = {
          //parseInt(ev.key)
          code: { ...vanillaProcedure, ...value, units: units },
          context: {
            value,
            field,
            fields,
            transaction
          }
        };
        onCodeEvent(update);
      };

      return { ...field, onKeyPress: handleTextChange };
    }
    return field;
  });
}

export type TransactionProps = {
  transaction: Accelerate.CQATransaction;
  codeSearch?: CQA.CodeSearchProps;
  invoice: Accelerate.CQAInvoice;
  cptFields: CMxCommonApp.CodeLayerField[];
  icdFields: CMxCommonApp.CodeLayerField[];
  modifierFields: CMxCommonApp.CodeLayerField[];
  onCodeEvent: (update: CMxCommonApp.GradeBenchCodeEvent) => void;
  onChange: (update: CMxCommonApp.GradeBenchSelectEvent) => void;
  onCodeRemove: (update: Accelerate.DeleteCQACodeParams) => void;
  onDeleteCQATransaction: (
    update: Accelerate.DeleteCQATransactionParams
  ) => void;
  projectConfiguration: CMxAPI.CQAProjectConfiguration;
  cqaCodeAssessmentRiskDictionaries: Accelerate.CQACodeAssessmentRiskDictionaries;
  editable?: boolean;
  workItemId?: number;
};

const Transaction = (props: TransactionProps) => {
  const {
    invoice,
    transaction,
    codeSearch,
    cptFields,
    icdFields,
    modifierFields,
    onCodeEvent,
    onChange,
    onCodeRemove,
    onDeleteCQATransaction,
    projectConfiguration,
    cqaCodeAssessmentRiskDictionaries,
    editable = true,
    workItemId
  } = props;

  const { procedure, diagnosis, modifier, tasks } = projectConfiguration;

  const procedureAllowNewCodes = procedure ?? tasks?.procedure?.allow_new_codes;
  const diagnosisAllowNewCodes = diagnosis ?? tasks?.diagnosis?.allow_new_codes;
  const modifierAllowNewCodes = modifier ?? tasks?.modifier?.allow_new_codes;

  const procedureVisible = procedure ?? tasks?.procedure?.visible;
  const diagnosisVisible = diagnosis ?? tasks?.diagnosis?.visible;
  const modifierVisible = modifier ?? tasks?.modifier?.visible;

  const cptFieldsWithHandlers = attachHandlersToFields(
    cptFields,
    transaction,
    invoice,
    'procedure',
    onChange,
    onCodeEvent,
    onCodeRemove
  );
  const icdFieldsWithHandlers = attachHandlersToFields(
    icdFields,
    transaction,
    invoice,
    'diagnosis',
    onChange,
    onCodeEvent,
    onCodeRemove
  );
  const modifierFieldsWithHandlers = attachHandlersToFields(
    modifierFields,
    transaction,
    invoice,
    'modifier',
    onChange,
    onCodeEvent,
    onCodeRemove
  );

  const codeLayerClassNames = ['standard-code-layer', 'code-row'];

  const cptItems: GenericCodeLayerProps[] = sortAndMap({
    array: transaction.procedures,
    sortKey: 'sequenceNumber',
    mapCallback: (cpt, index) => ({
      key: `cpt-${index}`,
      fields: cptFieldsWithHandlers,
      values: { ...cpt, codeType: 'procedure' },
      classNames: [
        ...codeLayerClassNames,
        cpt.codeAssessment
          ? 'selected-code-assessment'
          : 'select-code-assessment'
      ],
      draggable: procedureAllowNewCodes && editable,
      transactionId: transaction.id,
      cqaCodeAssessmentRiskDictionaries,
      editable,
      workItemId
    })
  });

  const icdItems: GenericCodeLayerProps[] = sortAndMap({
    array: transaction.diagnoses,
    sortKey: 'sequenceNumber',
    mapCallback: (icd, index) => ({
      key: `icd-${index}`,
      fields: icdFieldsWithHandlers,
      values: { ...icd, codeType: 'diagnosis' },
      classNames: [
        ...codeLayerClassNames,
        icd.codeAssessment
          ? 'selected-code-assessment'
          : 'select-code-assessment'
      ],
      draggable: diagnosisAllowNewCodes && editable,
      transactionId: transaction.id,
      cqaCodeAssessmentRiskDictionaries,
      editable,
      workItemId
    })
  });

  const modifierItems: GenericCodeLayerProps[] = sortAndMap({
    array: transaction.modifiers,
    sortKey: 'sequenceNumber',
    mapCallback: (modifier, index) => ({
      key: `modifier-${index}`,
      fields: modifierFieldsWithHandlers,
      values: { ...modifier, codeType: 'modifier' },
      classNames: [
        ...codeLayerClassNames,
        modifier.codeAssessment
          ? 'selected-code-assessment'
          : 'select-code-assessment'
      ],
      draggable: modifierAllowNewCodes && editable,
      transactionId: transaction.id,
      cqaCodeAssessmentRiskDictionaries,
      editable,
      workItemId
    })
  });

  const lookupRef = useRef(null);

  const searchResults = codeSearch?.searchResults?.[transaction.id] ?? [];

  const onSearch = (codeQuery: CMxCommonApp.CodeQuery) => {
    if (!editable) return;
    
    props.codeSearch.onCodeSearch({
      ...codeQuery,
      context: {
        transaction: { ...transaction }
      }
    });
  };

  const onCodeSelect = (codeInfo: Accelerate.CodeActionPayload) => {
    if (!editable) return;

    const isUnique = isCodeUniqueInCQATransaction(
      codeInfo.codeInfo,
      transaction
    );

    if (!isUnique) {
      return;
    }

    props.codeSearch.onCodeSearchSelection({
      codeInfo: codeInfo.codeInfo,
      context: {
        ...codeInfo.context,
        transaction: { ...transaction }
      }
    });
  };

  const handleCodeResequence = (
    action: CMxCommonApp.DroppedAction<Accelerate.CQACode>
  ) => {
    if (!action.dropCandidate || !action.dropTarget || !editable) {
      return;
    }

    onCodeEvent({
      code: {
        ...action.dropCandidate,
        sequenceNumber: action.dropTarget.sequenceNumber
      },
      context: {
        transaction: { ...transaction }
      }
    });
  };

  const canDropInto = (
    item1: any,
    item2: any,
    position: string,
    allItems: any[]
  ) => {
    if (!editable) return false;

    if (
      item1.codeType == item2.codeType &&
      item1.transactionId == item2.transactionId
    ) {
      return true;
    } else {
      return false;
    }
  };

  const onRemoveTransactionClick = (
    invoiceId: number,
    transactionId: number,
    original: boolean
  ) => {
    if (!editable) return;
    
    onDeleteCQATransaction({ invoiceId, transactionId, original });
  };

  const hasProcedureCodes = transaction.procedures.length > 0;

  //Display Procedure Lookup if procedure are active for the project and there is not a procedure code yet
  const incorrectCPTCount = transaction.procedures.filter(
    (code) =>
      cqaCodeAssessmentRiskDictionaries.procedure.rows.find(
        (row) => row.assessment == code?.codeAssessment?.assessment
      )?.category == 'INCORRECT'
  ).length;

  const correctOrNeutralCPTCount = transaction.procedures.filter(
    (code) =>
      cqaCodeAssessmentRiskDictionaries.procedure.rows.find(
        (row) => row.assessment == code?.codeAssessment?.assessment
      )?.category !== 'INCORRECT' || code?.codeAssessment == null
  ).length;

  // caseEvaluationLevel is driven by an option on the CQA collection/rule.
  // ML models need support for case level and charge level grading. In case
  // level grading a grader can link several procedures on the same transaction/charge,
  // so in this case cpt search is always made availble. If case caseEvaluationLevel
  // is set to false then the default functionality is understood as charge level grading.
  const displayCPTLookup =
    procedureAllowNewCodes &&
    (transaction.procedures.length == 0 ||
      incorrectCPTCount > correctOrNeutralCPTCount ||
      projectConfiguration.caseEvaluationLevel);
  //Display ICD Lookup if diagnosis are active for the project and the transaction has a CPT Code
  const displayICDLookup = diagnosisAllowNewCodes && (hasProcedureCodes || !procedure);
  //Display MOD Lookup if modifier are active for the project and the transaction has a Modifier Code
  const displayMODLookup = modifierAllowNewCodes && (hasProcedureCodes || !procedure);
  return (
    //Grade-bench-panel shares the invoice card with code-bench-panel-v2
    <InvoiceCard
      additionalClasses={[
        'codable-invoice',
        'qa-codable-invoice',
        'transaction-card',
        'gradeable-transaction',
        !editable && 'transaction-uneditable'
      ].filter(Boolean)}
      key={`transaction-${transaction.sequenceNumber}`}>
      <Box className="code-row charge-text">
        <span className="code-header-row">
          Charge {transaction.sequenceNumber}
        </span>
        {!transaction.original && editable && (
          <div className="charge-remove">
            <button
              className="charge-remove-btn"
              onClick={() =>
                onRemoveTransactionClick(
                  invoice.id,
                  transaction.id,
                  transaction.original
                )
              }>
              remove
            </button>
          </div>
        )}
      </Box>
      {
        procedureVisible && (
          <DraggableList
            items={cptItems}
            itemComponent={GenericCodeLayer as any}
            onDropHandler={handleCodeResequence}
            canDropOnto={canDropInto}
            droppable={editable}
            dragManager={draggedCodesManager}
            dataKey={'values'}
          />
        )
      }
      {displayCPTLookup && codeSearch && editable && (
        <Lookup
          ref={lookupRef}
          actionPrefix=""
          placeholderText={'Select procedure'}
          debounceTimeout={30}
          FieldDefinitions={[{ label: 'Procedure', value: 'cpt', key: 'CPT' }]}
          shouldShowResults={shouldShowResults}
          autoFocus={transaction.sequenceNumber == 1}
          onSearch={onSearch}
          onSearchSelection={onCodeSelect}
          searchResults={searchResults}
          SearchBarViewVersion={'SearchBarView2'}
        />
      )}
      {
        modifierVisible && (
          <DraggableList
            items={modifierItems}
            itemComponent={GenericCodeLayer as any}
            onDropHandler={handleCodeResequence}
            canDropOnto={canDropInto}
            droppable={editable}
            dragManager={draggedCodesManager}
            dataKey={'values'}
          />
        )
      }
      {displayMODLookup && codeSearch && editable && (
        <Lookup
          ref={lookupRef}
          actionPrefix=""
          placeholderText={'Select modifier'}
          debounceTimeout={30}
          FieldDefinitions={[
            { label: 'Modifier', value: 'modifier', key: 'MODIFIER' }
          ]}
          shouldShowResults={shouldShowResults}
          autoFocus={false}
          onSearch={onSearch}
          onSearchSelection={onCodeSelect}
          searchResults={searchResults}
          SearchBarViewVersion={'SearchBarView2'}
        />
      )}
      {
        diagnosisVisible && (
          <DraggableList
            items={icdItems}
            itemComponent={GenericCodeLayer as any}
            onDropHandler={handleCodeResequence}
            canDropOnto={canDropInto}
            droppable={editable}
            dragManager={draggedCodesManager}
            dataKey={'values'}
          />
        )
      }
      {displayICDLookup && codeSearch && editable && (
        <Lookup
          ref={lookupRef}
          actionPrefix=""
          placeholderText={'Select diagnosis'}
          debounceTimeout={30}
          FieldDefinitions={[{ label: 'Diagnosis', value: 'icd', key: 'ICD' }]}
          shouldShowResults={shouldShowResults}
          autoFocus={false}
          onSearch={onSearch}
          onSearchSelection={onCodeSelect}
          searchResults={searchResults}
          SearchBarViewVersion={'SearchBarView2'}
        />
      )}
    </InvoiceCard>
  );
};

const WorkflowValidationPanelAlert = ({
  validationResult
}: {
  validationResult?: WorkflowValidationResult | null;
}) => {
  const errors =
    validationResult?.isValid === false ? validationResult.errors ?? [] : [];

  if (!errors.length) {
    return null;
  }

  return (
    <div
      className="workflow-validation-panel-alert"
      role="alert"
      aria-live="polite">
      <div className="workflow-validation-panel-alert-title">
        Case cannot be saved yet
      </div>
      <ul className="workflow-validation-panel-alert-list">
        {errors.map((message, index) => (
          <li key={index}>{message}</li>
        ))}
      </ul>
    </div>
  );
};

const GradeBenchPanelGeneralized: React.FC<GradeBenchPanelProps> = (props) => {
  // Return grade-benchgroup

  const {
    gradeableInvoices,
    codeSearch,
    invoiceMetadataFields,
    cptFields,
    icdFields,
    modifierFields,
    onCodeUpdate,
    onCodeRemove,
    onCodeEvent,
    riskCategories,
    cqaCodeAssessmentRiskDictionaries,
    projectConfiguration = AmplifyState.defaultQARule.data
      .project_configuration,
    onCreateCQATransaction,
    onDeleteCQATransaction,
    gradingErrors,
    resolverValidationErrors,
    workflowValidationResult,
    editable,
    capabilities,
  } = props;

  // Get workitemId from the first invoice (set during loadCQAItemFromURL)
  const sdCaseId = gradeableInvoices[0]?.sdCaseId;
  const workitemId = gradeableInvoices[0]?.workitemId;
  const status = gradeableInvoices[0]?.status
  const invoiceItems = gradeableInvoices.map((invoice, invoiceIndex) => {
    const attachInvoiceToUpdate = (
      update: CMxCommonApp.GradeBenchSelectEvent
    ) => {
      update.context['invoice'] = invoice;
      update.context.riskCategories = riskCategories;
      onCodeUpdate({ ...update });
    };

    const codeSearchWithInvoice = {
      ...codeSearch,
      onCodeSearchSelection: (codeAction: Accelerate.CodeActionPayload) => {
        codeAction.context.invoice = invoice;
        codeSearch.onCodeSearchSelection(codeAction);
      }
    };

    const onCodeEventWithInvoice = (
      codeUpdate: CMxCommonApp.GradeBenchCodeEvent
    ) => {
      codeUpdate.context.invoice = invoice;
      onCodeEvent(codeUpdate);
    };

    const onAddTransactionClick = (invoice: Accelerate.CQAInvoice) => {
      onCreateCQATransaction({ invoiceId: invoice.id });
    };

    const transactionsOrdered = Array.from(
      invoice.transactions ?? []
    ).sort((t1, t2) => t1.sequenceNumber - t2.sequenceNumber);
    const txs = transactionsOrdered.map((transaction, index) => {
      return (
        <Transaction
          key={`transaction-${index + 1}-group`}
          invoice={invoice}
          transaction={transaction}
          codeSearch={codeSearchWithInvoice}
          cptFields={cptFields}
          icdFields={icdFields}
          modifierFields={modifierFields}
          onChange={attachInvoiceToUpdate}
          onCodeRemove={onCodeRemove}
          projectConfiguration={projectConfiguration}
          onCodeEvent={onCodeEventWithInvoice}
          onDeleteCQATransaction={onDeleteCQATransaction}
          cqaCodeAssessmentRiskDictionaries={cqaCodeAssessmentRiskDictionaries}
          editable={editable}
          workItemId={workitemId}
        />
      );
    });

    return {
      headerText: invoice['dateOfService'] ?? '',
      subheaderText: invoice['billingProviderName'] ?? '',
      body: (
        <div key={invoiceIndex} className="invoice-meta">
          <div className="invoice-meta-line invoice-meta-provider">
            <InvoiceMetadata
              fields={invoiceMetadataFields}
              invoice={invoice}
              editable={false}
            />
            {txs}
          </div>
          {!projectConfiguration.caseEvaluationLevel && editable && (
            <div
              key={`add-transaction-${invoiceIndex}`}
              className="add-transaction"
              onClick={() => onAddTransactionClick(invoice)}>
              <button className="add-transaction-btn">
                <CMXIcon
                  svgIconName={'PlusSign1'}
                  svgIconClass={''}
                  svgIconSize={'16'}
                  svgViewBox={'16'}
                />
                <span>&nbsp; Add transaction</span>
              </button>
              {gradingErrors.addTransaction && (
                <small className="add-transaction-error">
                  error adding transaction
                </small>
              )}
            </div>
          )}
          <WorkflowValidationPanelAlert
            validationResult={workflowValidationResult}
          />
        </div>
      )
    };
  });

  const [showResolverErrorsModal, setShowResolverErrorsModal] = useState(false);
  const hasResolverErrors = (resolverValidationErrors?.length ?? 0) > 0;

  useEffect(() => {
    if (hasResolverErrors) {
      setShowResolverErrorsModal(true);
    }
    if (!resolverValidationErrors?.length) {
      setShowResolverErrorsModal(false);
    }
  }, [hasResolverErrors, resolverValidationErrors?.length]);

  return (
    <Box className="code-bench-panelv2 grade-bench-panel">
      {conditionalUtils.capabilityValue(
        capabilities?.capabilities?.viewScoreDetail?.on
      ) &&
        (status === 'CQA_COMPLETE' || status === 'CQA_APPROVED') && (
          <ScoreDetailPanel sdCaseId={sdCaseId} />
        )}
      {sdCaseId && <CaseQuestionsForm sdWorkitemId={sdCaseId} evaluateFn={gradeBenchPanelActions.evaluateWorkflow} workItemId={workitemId} />}
      {showResolverErrorsModal && resolverValidationErrors && resolverValidationErrors.length > 0 && (
        <GradeBenchResolverModal
          validationErrors={resolverValidationErrors}
          onClose={() => setShowResolverErrorsModal(false)}
        />
      )}
      <HorizontalTabContainer items={invoiceItems} />
    </Box>
  );
};

export default GradeBenchPanelGeneralized;
